import { useCallback, useEffect, useRef, useState } from 'react';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { EventEmitter } from 'events';
import { TOKENS } from 'constants/index';
import TypedEmitter from 'typed-emitter';
import {
  CallAccessAttributes,
  ChatMessagePayload,
  ChatTypingPayload,
  HubPatient,
  HubPatientQueueResponse,
  IsProviderInQueueMessage,
  MutePatientMessage,
  PaymentRequestCreatedMessage,
  UpdatedUnavailableProviderStatusMessage,
} from 'types';
import { addPatient, removePatient, setPatients } from 'store/patientQueue';
import { useUserAgentInfo, useVideo } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { selectPatient } from 'store/patient';
import { selectWaitingRoomProfile } from 'store/waitingRoom';
import { isHipaaLink } from 'config/productProfile';

import { EventType, EventsMap, SignalrConnectionContext, SignalrConnectionEvents } from './context';

interface SignalrConnectionProviderProps {
  children?: JSX.Element;
}

declare global {
  interface Window {
    signalrConnection: React.MutableRefObject<HubConnection | null>;
  }
}

export function SignalrConnectionProvider({ children }: SignalrConnectionProviderProps) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);

  const patient = useAppSelector(selectPatient);
  const waitingRoomProfile = useAppSelector(selectWaitingRoomProfile);

  const signalrConnection = useRef<HubConnection | null>(null);
  const faxHubConnection = useRef<HubConnection | null>(null);

  // ✅ Attach signalrConnection to window for debugging
  useEffect(() => {
    window.signalrConnection = signalrConnection;
  }, [signalrConnection]);

  const prevClinicUserId = useRef<string | null>(null);

  const [isFetching, setIsFetching] = useState(false);
  const [signalrConnectionId, setSignalrConnectionId] = useState('');
  const { type, os, browser } = useUserAgentInfo();
  const eventsRef = useRef<TypedEmitter<SignalrConnectionEvents> | null>(null);

  if (!eventsRef.current) {
    eventsRef.current = new EventEmitter() as TypedEmitter<SignalrConnectionEvents>;
  }

  const events = eventsRef.current;

  const signalrEventRefs = useRef<EventsMap>({} as unknown as EventsMap);

  const addEventListener = (eventType: EventType, callback: () => void): void => {
    const targetEventListeners = signalrEventRefs.current[eventType];

    signalrEventRefs.current = targetEventListeners
      ? { ...signalrEventRefs.current, [eventType]: [...targetEventListeners, callback] }
      : { ...signalrEventRefs.current, [eventType]: [callback] };
  };

  const removeEventListener = (eventType: EventType, callback: () => void): void => {
    const targetEventListeners = signalrEventRefs.current[eventType];

    if (targetEventListeners) {
      signalrEventRefs.current = {
        ...signalrEventRefs.current,
        [eventType]: targetEventListeners.filter((eventListener) => eventListener !== callback),
      };
    }
  };

  const removeAllEventListeners = (): void => {
    signalrEventRefs.current = {} as unknown as EventsMap;
  };

  const internalEmitEvent = (eventType: EventType): void => {
    const targetEventListeners = signalrEventRefs.current[eventType];

    if (targetEventListeners) {
      targetEventListeners.forEach((eventListener) => {
        eventListener();
      });
    }
  };

  useEffect(() => {
    return () => {
      events.removeAllListeners();
      removeAllEventListeners();
    };
  }, []);

  useEffect(() => {
    if (!user && !patient && signalrConnection.current) {
      disconnect();
    }

    if (user || patient) {
      if ((!user || user?.clinicId === prevClinicUserId.current) && signalrConnection.current) {
        return;
      }

      if (user && user?.clinicId !== prevClinicUserId.current && signalrConnection.current) {
        disconnect();
      }

      prevClinicUserId.current = user?.clinicId ?? null;

      const providerId = user?.clinicUser.id ?? waitingRoomProfile!.providerId;
      const userName = user?.clinicUser.id ?? patient!.userName;
      const phoneNumber = user?.phoneNumber ?? patient?.phoneNumber ?? '';

      const queryString = {
        providerId,
        userName,
        phoneNumber,
        typeOfDevice: type,
        os: os,
        browser: browser,
      };
      const urlParams: string = new URLSearchParams(queryString).toString();

      const hubConnection: HubConnection = new HubConnectionBuilder()
        .withUrl(`${process.env['REACT_APP_HIPAA_LINK_API_URI']}patientsQueue?${urlParams}`)
        .withAutomaticReconnect()
        .build();

      listenQueueHub(hubConnection);

      hubConnection
        .start()
        .then(function () {
          signalrConnection.current = hubConnection;
          window.signalrConnection = { current: hubConnection };
          setSignalrConnectionId(hubConnection.connectionId!);
        })
        .catch(function (err) {
          console.error('❌ SignalR Connection Failed:', err);
        });

      hubConnection.onreconnected(() => {
        internalEmitEvent('reconnected');
      });

      hubConnection.onclose(() => {
        disconnect();
        internalEmitEvent('disconnected');
      });
    }
  }, [user, prevClinicUserId, patient, waitingRoomProfile?.providerId]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  const getFaxHubToken = () => {
    try {
      const tokensJson = window.localStorage.getItem(TOKENS);
      const parsedTokens = tokensJson ? JSON.parse(tokensJson) : null;
      return parsedTokens?.token ?? '';
    } catch {
      return '';
    }
  };

  useEffect(() => {
    if (!user || isHipaaLink) return;

    const connection = new HubConnectionBuilder()
      .withUrl(`${process.env['REACT_APP_HIPAA_LINK_API_URI']}faxHub`, {
        accessTokenFactory: () => {
          const token = getFaxHubToken();
          return token;
        },
      })
      .withAutomaticReconnect()
      .build();

    connection.on('FaxStatusUpdated', (message) => {
      events.emit('faxStatusUpdated', message);
    });

    connection.on('FaxReceived', () => {
      events.emit('faxReceived');
    });

    connection
      .start()
      .then(() => {
        faxHubConnection.current = connection;
      })
      .catch((err) => {
        console.error('❌ FaxHub connection failed', err);
      });

    return () => {
      connection.stop();
      faxHubConnection.current = null;
    };
  }, [user]);

  const listenQueueHub = (hubConnection: HubConnection) => {
    if (hubConnection) {
      hubConnection.on('callPreparing', () => {
        events.emit('callPreparing');
      });

      hubConnection.on('joinCall', (roomName: string) => {
        events.emit('joinCall', roomName, hubConnection.connectionId!);
      });

      hubConnection.on('Transferred', (newRoomUrl: string) => {
        events.emit('Transferred', newRoomUrl);
      });

      hubConnection.on('Requeued', () => {
        internalEmitEvent('requeued');
      });

      hubConnection.on('disconnected', () => {
        internalEmitEvent('disconnected');
      });

      hubConnection.on('removedFromCall', () => {
        internalEmitEvent('removedFromCall');
      });

      hubConnection.on('isProviderInQueueSent', (message: IsProviderInQueueMessage) => {
        events.emit('isProviderInQueueSent', message.isProviderInQueue);
      });

      hubConnection.on('updatedStatusDataSent', () => {
        events.emit('updatedStatusDataSent');
      });

      hubConnection.on('muted', (message: MutePatientMessage) => {
        events.emit('muted', message.muted);
      });

      hubConnection.on('emdrStarted', () => {
        internalEmitEvent('emdrStarted');
      });

      hubConnection.on('whiteboardStarted', () => {
        internalEmitEvent('whiteboardStarted');
      });

      hubConnection.on('PaymentRequestCancelled', () => {
        internalEmitEvent('paymentRequestCancelled');
      });

      hubConnection.on('PaymentRequestSucceeded', () => {
        internalEmitEvent('paymentRequestSucceeded');
      });

      hubConnection.on('PaymentRequestCreated', (message: PaymentRequestCreatedMessage) => {
        events.emit('paymentRequestCreated', message);
      });

      hubConnection.on('updatedUnavailableProviderStatus', (message: UpdatedUnavailableProviderStatusMessage) => {
        events.emit('updatedUnavailableProviderStatus', message.unavailable);
      });

      hubConnection.on('ReceivePrivateMessage', (message: ChatMessagePayload) => {
        events.emit('receivePrivateMessage', message);
      });

      hubConnection.on('ReceiveRoomMessage', (message: ChatMessagePayload) => {
        events.emit('receiveRoomMessage', message);
      });

      hubConnection.on('PrivateTypingStarted', (message: ChatTypingPayload) => {
        events.emit('privateTypingStarted', message);
      });

      hubConnection.on('PrivateTypingEnded', (message: ChatTypingPayload) => {
        events.emit('privateTypingEnded', message);
      });

      hubConnection.on('RoomTypingStarted', (message: ChatTypingPayload) => {
        events.emit('roomTypingStarted', message);
      });

      hubConnection.on('RoomTypingEnded', (message: ChatTypingPayload) => {
        events.emit('roomTypingEnded', message);
      });

      if (user) {
        hubConnection.on('patientsListSent', (data: HubPatientQueueResponse) => {
          dispatch(setPatients(data.patients));
        });

        hubConnection.on('userJoined', (data: HubPatient) => {
          if (user?.clinicUser.id !== data.userName) {
            events.emit('userJoined', data);
            dispatch(addPatient(data));
          }
        });

        hubConnection.on('userLeft', (data: HubPatient) => {
          dispatch(removePatient(data));
        });
      }
    }
  };

  const getPatientsList = useCallback((): void => {
    if (!signalrConnection.current) {
      console.warn('⚠️ Cannot fetch patients — SignalR connection not ready');
      return;
    }

    signalrConnection.current.invoke('GetPatientsList').catch((err) => {
      console.error('❌ Failed to invoke GetPatientsList:', err);
    });
  }, [signalrConnection]);

  const getIsProviderConnectedToQueue = useCallback((): void => {
    signalrConnection.current!.invoke('GetIsProviderConnectedToQueue');
  }, [signalrConnection]);

  const callEnded = useCallback((): void => {
    internalEmitEvent('callEnded');
  }, []);

  const getToken = useCallback(
    async ({ identity, roomId }: { identity: string; roomId: string }): Promise<string> => {
      setIsFetching(true);
      return signalrConnection
        .current!.invoke('GetToken', identity, roomId)
        .then((token: string) => {
          setIsFetching(false);
          return token;
        })
        .catch((e) => {
          console.error('getToken failed: ', e);
          setIsFetching(false);
          return '';
        });
    },
    [signalrConnection],
  );

  const invitePatient = useCallback(
    ({ connectionId, roomId }: { connectionId: string; roomId: string }): void => {
      signalrConnection.current?.invoke('InvitePatient', { connectionId, roomId });
    },
    [signalrConnection],
  );

  const notifyCallPreparing = useCallback(
    (connectionId: string): void => {
      signalrConnection.current?.invoke('NotifyCallPreparing', connectionId);
    },
    [signalrConnection],
  );

  const joinRoomChat = useCallback(
    async (roomName: string): Promise<void> => {
      if (!signalrConnection.current || !roomName) return;
      await signalrConnection.current.invoke('JoinRoomChat', roomName);
    },
    [signalrConnection],
  );

  const sendPrivateMessage = useCallback(
    async ({
      targetConnectionId,
      message,
      clientMessageId,
    }: {
      targetConnectionId: string;
      message: string;
      clientMessageId?: string;
    }): Promise<void> => {
      if (!signalrConnection.current) return;
      await signalrConnection.current.invoke('SendPrivateMessage', targetConnectionId, message, clientMessageId);
    },
    [signalrConnection],
  );

  const sendPrivateMessageByUniqueName = useCallback(
    async ({
      uniqueName,
      message,
      clientMessageId,
    }: {
      uniqueName: string;
      message: string;
      clientMessageId?: string;
    }): Promise<void> => {
      if (!signalrConnection.current || !uniqueName) return;
      await signalrConnection.current.invoke('SendPrivateMessageByUniqueName', uniqueName, message, clientMessageId);
    },
    [signalrConnection],
  );

  const sendRoomMessage = useCallback(
    async ({ roomName, message, clientMessageId }: { roomName: string; message: string; clientMessageId?: string }) => {
      if (!signalrConnection.current || !roomName) return;
      await signalrConnection.current.invoke('SendRoomMessage', roomName, message, clientMessageId);
    },
    [signalrConnection],
  );

  const startTypingPrivate = useCallback(
    async (targetConnectionId: string): Promise<void> => {
      if (!signalrConnection.current || !targetConnectionId) return;
      await signalrConnection.current.invoke('StartTypingPrivate', targetConnectionId);
    },
    [signalrConnection],
  );

  const endTypingPrivate = useCallback(
    async (targetConnectionId: string): Promise<void> => {
      if (!signalrConnection.current || !targetConnectionId) return;
      await signalrConnection.current.invoke('EndTypingPrivate', targetConnectionId);
    },
    [signalrConnection],
  );

  const startTypingRoom = useCallback(
    async (roomName: string): Promise<void> => {
      if (!signalrConnection.current || !roomName) return;
      await signalrConnection.current.invoke('StartTypingRoom', roomName);
    },
    [signalrConnection],
  );

  const endTypingRoom = useCallback(
    async (roomName: string): Promise<void> => {
      if (!signalrConnection.current || !roomName) return;
      await signalrConnection.current.invoke('EndTypingRoom', roomName);
    },
    [signalrConnection],
  );

  const removeParticipantFromCall = useCallback(
    (connectionId: string): void => {
      signalrConnection.current?.invoke('RemoveParticipantFromCall', connectionId);
    },
    [signalrConnection],
  );

  const isRequeuing = useRef<boolean>(false);

  const requeuePatient = useCallback(
    (connectionId: string): void => {
      isRequeuing.current = true;
      signalrConnection.current?.invoke('RequeuePatient', connectionId);
      setTimeout(() => {
        isRequeuing.current = false;
      }, 3000);
    },
    [signalrConnection],
  );

  const mutePatientInCall = useCallback(
    (connectionId: string, muted: boolean): void => {
      signalrConnection.current?.invoke('MutePatient', { patientConnectionId: connectionId, muted });
    },
    [signalrConnection],
  );

  const updateCallData = useCallback(
    ({ callAccesses }: { callAccesses: CallAccessAttributes }): void => {
      signalrConnection.current?.invoke('UpdateCallData', {
        callAccess: callAccesses,
        deviceConfigs: {
          type,
          operationSystem: os,
          browser,
        },
      });
    },
    [signalrConnection],
  );

  const updateUnavailableProviderStatus = useCallback(
    (unavailable: boolean): void => {
      signalrConnection.current?.invoke('UpdateUnavailableProviderStatus', unavailable);
    },
    [signalrConnection],
  );

  const removePatientFromQueue = useCallback(
    (connectionId: string): void => {
      signalrConnection.current?.invoke('RemovePatientFromQueue', connectionId).catch((err) => {
        console.error('❌ Failed to invoke RemovePatientFromQueue:', err);
      });
    },
    [signalrConnection],
  );

  const startEmdr = useCallback((): void => {
    signalrConnection.current?.invoke('EmdrStart');
  }, [signalrConnection]);

  const startWhiteboard = useCallback((): void => {
    signalrConnection.current?.invoke('WhiteboardStart');
  }, [signalrConnection]);

  const cancelRequestPaymentByProvider = useCallback((): void => {
    signalrConnection.current?.invoke('CancelRequestPayment');
  }, []);

  const cancelRequestPaymentByClient = useCallback((): void => {
    signalrConnection.current?.invoke('CancelRequestPaymentByClient');
  }, []);

  const setSuccessRequestPayment = useCallback((): void => {
    signalrConnection.current?.invoke('SetSuccessPayment');
  }, []);

  const disconnect = useCallback((): void => {
    signalrConnection.current?.stop();
    signalrConnection.current = null;
    setSignalrConnectionId('');
  }, []);

  return (
    <SignalrConnectionContext.Provider
      value={{
        signalrConnection,
        getPatientsList,
        getIsProviderConnectedToQueue,
        getToken,
        joinRoomChat,
        sendPrivateMessage,
        sendPrivateMessageByUniqueName,
        sendRoomMessage,
        startTypingPrivate,
        endTypingPrivate,
        startTypingRoom,
        endTypingRoom,
        invitePatient,
        notifyCallPreparing,
        removeParticipantFromCall,
        requeuePatient,
        isRequeuing,
        mutePatientInCall,
        updateCallData,
        updateUnavailableProviderStatus,
        removePatientFromQueue,
        startEmdr,
        startWhiteboard,
        cancelRequestPaymentByClient,
        cancelRequestPaymentByProvider,
        setSuccessRequestPayment,
        disconnect,
        isFetching,
        events,
        signalrConnectionId,
        callEnded,
        addEventListener,
        removeEventListener,
        removeAllEventListeners,
        sendMessage: async (event: string, data: any) => {
          if (!signalrConnection.current) return;

          try {
            await signalrConnection.current.invoke(event, data);
          } catch (err) {
            console.warn(`⚠️ SignalR invoke failed for ${event}`);
            console.error(err);
          }
        },
      }}
    >
      {children}
    </SignalrConnectionContext.Provider>
  );
}
